import React from 'react'
import { useDispatch, useSelector } from 'react-redux';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import { selectFilterData } from '../../mainFilter/selectors';
import { selectSecondaryFilter } from '../../../features/secondaryFilter/secondaryFilterSlice';
import { pushEvent } from '../../../utilities/analytics';
import { MOREIN_COUNTRY_CLICK, REMOVE_CITY_CLICK } from '../../../constants/analyticEvents';
import { updateMainFilter } from '../../mainFilter/mainFilterSlice';
import { scrollToResults, searchInOneDestination } from '../../../utilities/helpers';
import { convertParamsToHash, getParamsFromUrl } from '../../../utilities/urlResolver';
import { t } from '../../../utilities/translator';
import { setLastChangedFilter } from '../../../appSlice';
import { clearPrefetchDeals } from '../../dealsList/prefetchDealsSlice';
import { prepareSearchRequest } from '../../../features/dealsList/dealsListSlice';
import LocationIcons from './../DealItem/LocationIcons';

const DealSettings = ({
  height,
  dealDestination,
  onClose,
  owSettingsBtn,
  hideSettings,
  dealItem,
}) => {
  const dispatch = useDispatch(null);

  const {
    id,
    name: destination,
    atlas_top: country,
    atlas_top_id: countryId,
  } = dealDestination;

  const mainFilter = useSelector(selectFilterData);
  const secondaryFilter = useSelector(selectSecondaryFilter);
  const {
    searchEngine: { where },
  } = mainFilter;
  // req.engine.where is needed for the case of selected 'what' theme, bc 'where' from the mainFilter is not right in that case
  const req = prepareSearchRequest({
    mainFilter: mainFilter.searchEngine,
    secondaryFilter: secondaryFilter.data
  });
  const activeWhereDest = where.destinations.filter(({ active }) => active);
  const isEveryWhere = (activeWhereDest.length === 0) && !req?.engine?.where?.length;

  const removeDestination = (e) => {
    e.stopPropagation();

    pushEvent(REMOVE_CITY_CLICK);

    dispatch(clearPrefetchDeals());
    if (isEveryWhere) {
      let updatedDestinations = where.destinations.map((destination) => ({
        ...destination,
        active: destination.id !== id,
      }));

      const destinations = updatedDestinations;
      const updated = where.destinationsGroups.map((group) => {
        if (!group.active) return group;

        const isUncheck = destinations
          .filter(
            (destination) =>
              Array.isArray(destination.destinations_groups) &&
              destination.destinations_groups.some((id) => id === group.id)
          )
          .some(({ active }) => !active);

        if (isUncheck) {
          return { ...group, active: false };
        }
        return group;
      });

      dispatch(
        updateMainFilter({
          updatedValue: {
            destinations: updatedDestinations,
            destinationsGroups: updated,
          },
          filter: "where",
        })
      );
    } else {
      let updatedDestinations = where.destinations;
      if (req?.engine?.where?.length && !activeWhereDest.length) {
        updatedDestinations = where.destinations.map((destination) => ({
          ...destination,
          active: (destination.id !== id && req.engine.where.indexOf(destination.id) > -1) ? true : false,
        }));
      } else {
        updatedDestinations = where.destinations.map((destination) => ({
          ...destination,
          active: destination.id !== id ? destination.active : false,
        }));
      }

      const destinations = updatedDestinations;
      const updated = where.destinationsGroups.map((group) => {
        if (!group.active) return group;

        const isUncheck = destinations
          .filter(
            (destination) =>
              Array.isArray(destination.destinations_groups) &&
              destination.destinations_groups.some((id) => id === group.id)
          )
          .some(({ active }) => !active);

        if (isUncheck) {
          return { ...group, active: false };
        }
        return group;
      });

      dispatch(
        updateMainFilter({
          updatedValue: {
            destinations: updatedDestinations,
            destinationsGroups: updated,
          },
          filter: "where",
        })
      );
    }
    dispatch(setLastChangedFilter('where'));

    scrollToResults();
  };

  const searchInCountry = (e) => {
    e.stopPropagation();

    const paramsFromUrl = getParamsFromUrl();
    delete paramsFromUrl.destinationsGroups;
    delete paramsFromUrl.destinations;

    const updatedHash = convertParamsToHash({
      ...paramsFromUrl,
      atlasTop: countryId
    });

    pushEvent(MOREIN_COUNTRY_CLICK, { countryId });

    window.open(
      `${window.location.origin}${window.location.pathname}#${updatedHash}&moreIn=country`,
      "_blank"
    );
  };

  return (
    <div className="card-face back rounded-xl shadow-lg overflow-hidden flex flex-col justify-end" style={{ height }}>
      <LazyLoadImage
        src={dealDestination.photo}
        className="w-full h-full object-cover overflow-auto"
        useIntersectionObserver
        delayTime={500}
        alt="Destination photo"
      />
      <LocationIcons
        showSettingsBtn={owSettingsBtn}
        onSettingsClick={onClose}
        hideSettings={hideSettings}
        dealItem={dealItem}
      />
      <div className="h-[220px] p-3 pt-5">
        <div className="flex items-center justify-between mb-5">
          <span className="label-medium leading-none font-medium text-primary">{t("dealItem.customize_your_search")}</span>
          <i className="ri-close-line text-xl leading-none cursor-pointer" onClick={onClose} />
        </div>
        <div className="flex flex-col gap-2 leading-none">
          <button onClick={searchInOneDestination(id)} className="flex items-center text-sm border-none rounded bg-shark-50 gap-2 px-4 py-3">
            <i className="ri-map-pin-line text-xl leading-none" />
            <span className="truncate">{t("dealItem.search_in", { destination_name: destination })}</span>
          </button>
          <button onClick={searchInCountry} className="flex items-center text-sm border-none rounded bg-shark-50 gap-2 px-4 py-3">
            <i className="ri-map-2-line text-xl leading-none" />
            <span className="truncate">{t("dealItem.search_in", { destination_name: country })}</span>
          </button>
          <button onClick={removeDestination} className="flex items-center text-sm border-none rounded bg-shark-50 gap-2 px-4 py-3">
            <i className="ri-eye-off-line text-xl leading-none" />
            <span className="truncate">{t("dealItem.remove")} {destination}</span>
          </button>
        </div>
      </div>
    </div>
  )
}

export default DealSettings
